/**
 * Tokenomics Studio Page — Vertical 5 (Tokenomics-as-a-Service B2B).
 *
 * Multi-token issuer workspace. Project list on the left, project detail on
 * the right. Detail tabs: Overview, Holders (real chain), Unlocks + Simulate,
 * API Keys.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, Trash2, RefreshCw, Copy, Check, AlertCircle,
  Globe, Key, ChevronRight, ExternalLink, Calendar, BarChart2,
  Sparkles, BookOpen, ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import {
  listTokenomicsProjects, createTokenomicsProject, getTokenomicsProject,
  updateTokenomicsProject, deleteTokenomicsProject,
  addTokenomicsToken, removeTokenomicsToken,
  addTokenomicsUnlock, removeTokenomicsUnlock,
  getTokenomicsHolders, simulateTokenomicsUnlock,
  issueTokenomicsApiKey, revokeTokenomicsApiKey,
  setTokenomicsExplorerKey, removeTokenomicsExplorerKey,
} from '../services/api';
import { InfoTip } from '../components/ui';

// Chains where the holder analysis pipeline supports a BYO explorer API key.
// Matches SUPPORTED_EXPLORER_CHAINS on the backend.
const EXPLORER_KEY_CHAINS = [
  { id: 'ethereum',  label: 'Ethereum',  provider: 'Etherscan'      },
  { id: 'polygon',   label: 'Polygon',   provider: 'Polygonscan'    },
  { id: 'bsc',       label: 'BNB Chain', provider: 'BscScan'        },
  { id: 'arbitrum',  label: 'Arbitrum',  provider: 'Arbiscan'       },
  { id: 'base',      label: 'Base',      provider: 'Basescan'       },
  { id: 'optimism',  label: 'Optimism',  provider: 'Optimistic Etherscan' },
  { id: 'avalanche', label: 'Avalanche', provider: 'Snowscan'       },
  { id: 'blast',     label: 'Blast',     provider: 'Blastscan'      },
  { id: 'linea',     label: 'Linea',     provider: 'Lineascan'      },
  { id: 'scroll',    label: 'Scroll',    provider: 'Scrollscan'     },
];

const NETWORKS = [
  'ethereum', 'base', 'arbitrum', 'optimism', 'polygon',
  'bsc', 'avalanche', 'blast', 'linea', 'scroll',
];

function PlanBadge({ plan }) {
  const cls = plan === 'enterprise' ? 'bg-violet-500/15 text-violet-300 border-violet-500/30'
    : plan === 'growth' ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
    : 'bg-zinc-700/40 text-zinc-300 border-zinc-600';
  return <span className={`px-2 py-0.5 text-xs rounded border ${cls}`}>{plan}</span>;
}

function StatPill({ label, value, hint }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-100 mt-1">{value ?? <span className="text-zinc-500">—</span>}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function PendingBanner({ reason }) {
  return (
    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-sm">
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        <div className="font-medium">Data unavailable</div>
        <div className="text-amber-300/80 mt-0.5">{reason || 'No real chain data available right now.'}</div>
      </div>
    </div>
  );
}

// ─── About card (collapsible explainer) ────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-zinc-800/50 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-teal-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-zinc-100">What is this tool — and how do I use it?</div>
            <div className="text-[11px] text-zinc-500">Plain-English walkthrough · 30 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-zinc-800 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-teal-300 uppercase tracking-wider mb-1.5">The problem</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              You issue tokens. Investors, market makers, and listing teams keep asking: <em>how concentrated are your holders, when are the next unlocks, what's the price impact if the team unlock dumps?</em> Today you answer that ad-hoc in Telegram with screenshots. There's no shared workspace, no public report URL, no programmatic API to embed on your IR site.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-teal-300 uppercase tracking-wider mb-1.5">What this tool does</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Create a <span className="text-zinc-100">project</span>, add your token contracts (across multiple chains), schedule the <span className="text-zinc-100">unlock calendar</span>, and the workspace shows: live <span className="text-zinc-100">holder concentration</span> (top-10, top-50, Gini), an <span className="text-zinc-100">unlock-impact simulator</span> using real Uniswap V2 constant-product math, a <span className="text-zinc-100">shareable public report URL</span> for investors, and <span className="text-zinc-100">API keys</span> so you can embed the data on your own IR/DD site.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-teal-300 uppercase tracking-wider mb-1.5">How to use it in 4 steps</div>
            <ol className="text-sm text-zinc-300 leading-relaxed space-y-1.5 list-decimal pl-5">
              <li>Create a project (pick a plan — controls how many tokens + API keys you can have).</li>
              <li>Add one or more <span className="text-zinc-100">tokens</span> (contract + chain + decimals).</li>
              <li>Add your <span className="text-zinc-100">unlock calendar</span> — vesting cliffs, team unlocks, treasury moves. Each entry includes amount + date + recipient + notes.</li>
              <li>Run the <span className="text-zinc-100">simulator</span> with your real DEX pool reserves to see the price-impact of any unlock under single-shot and staggered-sell scenarios. Toggle <span className="text-zinc-100">public report</span> to share a URL with investors. Issue <span className="text-zinc-100">API keys</span> to embed the data on your own site.</li>
            </ol>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">How it works:</span> The simulator uses Uniswap V2 constant-product math (also a useful approximation for V3 near current price). It needs <span className="font-semibold">your real pool reserves</span> as input — we don't fabricate liquidity. Holder data comes from chain explorers; if an explorer is rate-limited or doesn't expose top-holders, the row shows <em>"Data unavailable"</em> instead of inventing numbers.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar: project list + create ────────────────────────────────────────

function ProjectList({ projects, selectedId, onSelect, onCreate }) {
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('starter');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreate({ name: name.trim(), plan });
      setName(''); setShowNew(false);
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-zinc-200">Projects</h2>
        <button onClick={() => setShowNew(v => !v)} className="text-zinc-300 hover:text-white">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {showNew && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 mb-3 space-y-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Project name"
            className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100" />
          <select value={plan} onChange={e => setPlan(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100">
            <option value="starter">Starter — 1 token contract</option>
            <option value="growth">Growth — up to 5 token contracts</option>
            <option value="enterprise">Enterprise — up to 50 token contracts</option>
          </select>
          <p className="text-[10px] text-zinc-500 leading-snug">
            A "token contract" = one ERC-20 on one chain. Most single-token projects need <strong>Starter</strong>. Multi-chain deployments or DAOs with several tokens (gov + treasury + LP) need <strong>Growth</strong>. <strong>Enterprise</strong> is for agencies / large protocols.
          </p>
          <button onClick={submit} disabled={busy || !name.trim()}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm rounded py-1.5">
            {busy ? 'Creating…' : 'Create project'}
          </button>
        </div>
      )}
      <div className="space-y-1 max-h-[600px] overflow-y-auto">
        {projects.length === 0 && (
          <div className="text-xs text-zinc-500 py-4 text-center">No projects yet. Create one above.</div>
        )}
        {projects.map(p => (
          <button key={p.projectId} onClick={() => onSelect(p.projectId)}
            className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 ${selectedId === p.projectId ? 'bg-zinc-800 text-zinc-100' : 'hover:bg-zinc-800/50 text-zinc-300'}`}>
            <Building2 className="w-4 h-4 text-zinc-500" />
            <span className="flex-1 text-sm truncate">{p.name}</span>
            <ChevronRight className="w-3 h-3 text-zinc-500" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: Overview ─────────────────────────────────────────────────────────

function OverviewTab({ project, onUpdate, onAddToken, onRemoveToken, onTogglePublic, onDelete }) {
  const [tokenForm, setTokenForm] = useState({ symbol: '', name: '', network: 'ethereum', contractAddress: '', decimals: 18 });
  const [busy, setBusy] = useState(false);
  const publicUrl = `${window.location.origin}/tokenomics/r/${project.publicSlug}`;
  const [copied, setCopied] = useState(false);

  async function copyShare() {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function submitToken() {
    if (!tokenForm.symbol || !tokenForm.contractAddress) return;
    setBusy(true);
    try {
      await onAddToken({ ...tokenForm });
      setTokenForm({ symbol: '', name: '', network: 'ethereum', contractAddress: '', decimals: 18 });
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Plan" value={project.plan} />
        <StatPill label="Token contracts" value={(project.tokens || []).length} hint="ERC-20 on a chain" />
        <StatPill label="Unlocks scheduled" value={(project.unlocks || []).length} />
        <StatPill label="API keys" value={(project.apiKeys || []).length} />
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Public report URL</h3>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <input readOnly value={publicUrl}
            className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-400 font-mono" />
          <button onClick={copyShare} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs px-3 py-1.5 rounded flex items-center gap-1">
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={!!project.publicReportEnabled}
            onChange={e => onTogglePublic(e.target.checked)} />
          Make this report publicly accessible (investors can view without an account)
        </label>
        {!project.publicReportEnabled && (
          <p className="text-xs text-zinc-500 mt-2">URL above will return 404 until enabled.</p>
        )}
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3">Tokens</h3>
        <div className="space-y-2 mb-4">
          {(project.tokens || []).length === 0 && (
            <div className="text-xs text-zinc-500">No tokens yet. Add one below.</div>
          )}
          {(project.tokens || []).map(t => (
            <div key={`${t.network}-${t.contractAddress}`} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded p-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 font-semibold">{t.symbol} <span className="text-zinc-500 font-normal text-xs">{t.name}</span></div>
                <div className="text-xs text-zinc-500 font-mono truncate">{t.network} · {t.contractAddress}</div>
              </div>
              <button onClick={() => onRemoveToken({ network: t.network, contractAddress: t.contractAddress })}
                className="text-zinc-500 hover:text-rose-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input value={tokenForm.symbol} onChange={e => setTokenForm(f => ({ ...f, symbol: e.target.value }))} placeholder="Symbol"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input value={tokenForm.name} onChange={e => setTokenForm(f => ({ ...f, name: e.target.value }))} placeholder="Name"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <select value={tokenForm.network} onChange={e => setTokenForm(f => ({ ...f, network: e.target.value }))}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100">
            {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <input value={tokenForm.contractAddress} onChange={e => setTokenForm(f => ({ ...f, contractAddress: e.target.value }))} placeholder="0x… contract"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100 md:col-span-1" />
          <input type="number" value={tokenForm.decimals} onChange={e => setTokenForm(f => ({ ...f, decimals: parseInt(e.target.value || '18', 10) }))} placeholder="Decimals"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
        </div>
        <button onClick={submitToken} disabled={busy || !tokenForm.symbol || !tokenForm.contractAddress}
          className="mt-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded px-3 py-1.5">
          {busy ? 'Adding…' : 'Add token'}
        </button>
      </div>

      <div className="bg-zinc-900/50 border border-rose-900/30 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-rose-300 mb-2">Danger zone</h3>
        <p className="text-xs text-zinc-400 mb-3">Deleting a project removes all tokens, unlocks, and API keys. This cannot be undone.</p>
        <button onClick={() => { if (window.confirm('Delete this project permanently?')) onDelete(); }}
          className="bg-rose-600/80 hover:bg-rose-600 text-white text-xs rounded px-3 py-1.5">
          Delete project
        </button>
      </div>
    </div>
  );
}

// ─── Tab: Holders ──────────────────────────────────────────────────────────

function HoldersTab({ project }) {
  const [selected, setSelected] = useState(project.tokens?.[0] || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await getTokenomicsHolders(project.projectId, { network: selected.network, contractAddress: selected.contractAddress });
      setData(r?.data || r);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [selected?.contractAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!project.tokens || project.tokens.length === 0) {
    return <div className="text-sm text-zinc-500">Add a token first to run holder analysis.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          value={selected ? `${selected.network}|${selected.contractAddress}` : ''}
          onChange={e => {
            const [n, a] = e.target.value.split('|');
            setSelected(project.tokens.find(t => t.network === n && t.contractAddress === a));
          }}
          className="bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-100"
        >
          {project.tokens.map(t => (
            <option key={`${t.network}-${t.contractAddress}`} value={`${t.network}|${t.contractAddress}`}>
              {t.symbol} · {t.network}
            </option>
          ))}
        </select>
        <button onClick={load} disabled={loading} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm rounded px-3 py-1.5 flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {!data && loading && <div className="text-sm text-zinc-500">Loading from chain explorer…</div>}
      {data?.status === 'pending' && <PendingBanner reason={data.reason} />}
      {data?.status === 'ok' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Top 10 concentration" value={data.metrics.top10ConcentrationPct !== null ? `${data.metrics.top10ConcentrationPct}%` : null} />
            <StatPill label="Top 50 concentration" value={data.metrics.top50ConcentrationPct !== null ? `${data.metrics.top50ConcentrationPct}%` : null} />
            <StatPill label="Gini coefficient" value={data.metrics.giniCoefficient} hint="0 = even, 1 = one wallet" />
            <StatPill label="Sample size" value={data.metrics.sampleSize} hint="Top-N from explorer" />
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2 border-b border-zinc-800 text-xs uppercase tracking-wide text-zinc-500">Top holders</div>
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 bg-zinc-950">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Address</th>
                  <th className="text-left px-3 py-2">Label</th>
                  <th className="text-right px-3 py-2">Balance</th>
                  <th className="text-right px-3 py-2">% of supply</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.top.slice(0, 25).map((h, i) => (
                  <tr key={h.address}>
                    <td className="px-3 py-1.5 text-zinc-500">{i + 1}</td>
                    <td className="px-3 py-1.5 text-zinc-300 font-mono text-xs">{h.address.slice(0, 10)}…{h.address.slice(-6)}</td>
                    <td className="px-3 py-1.5 text-zinc-400">{h.label || '—'}</td>
                    <td className="px-3 py-1.5 text-zinc-300 text-right">{h.balance?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-1.5 text-zinc-300 text-right">{h.percentage !== null ? `${h.percentage}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-zinc-500 space-y-1">
            <div>{data.disclosures?.decimalsCaveat}</div>
            <div>{data.disclosures?.sampleCaveat}</div>
            <div>{data.disclosures?.labelCaveat}</div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Unlocks + Simulate ───────────────────────────────────────────────

function UnlocksTab({ project, onAddUnlock, onRemoveUnlock }) {
  const [form, setForm] = useState({ tokenSymbol: project.tokens?.[0]?.symbol || '', eventDate: '', amount: '', percentOfSupply: '', recipient: '', notes: '' });
  const [sim, setSim] = useState({ unlockId: null, amount: '', tokenReserve: '', quoteReserve: '', quoteSymbol: 'USDC', slices: 12, recoveryFactor: 0.5, result: null, loading: false });

  async function submitUnlock() {
    if (!form.tokenSymbol || !form.eventDate || !(Number(form.amount) > 0)) return;
    await onAddUnlock({
      tokenSymbol: form.tokenSymbol,
      eventDate: form.eventDate,
      amount: Number(form.amount),
      percentOfSupply: form.percentOfSupply ? Number(form.percentOfSupply) : null,
      recipient: form.recipient || null,
      notes: form.notes || '',
    });
    setForm(f => ({ ...f, eventDate: '', amount: '', percentOfSupply: '', recipient: '', notes: '' }));
  }

  async function runSim() {
    if (!(Number(sim.amount) > 0) || !(Number(sim.tokenReserve) > 0) || !(Number(sim.quoteReserve) > 0)) return;
    setSim(s => ({ ...s, loading: true, result: null }));
    try {
      const r = await simulateTokenomicsUnlock(project.projectId, {
        amount: Number(sim.amount),
        reserves: {
          tokenReserve: Number(sim.tokenReserve),
          quoteReserve: Number(sim.quoteReserve),
          quoteSymbol: sim.quoteSymbol,
        },
        slices: Number(sim.slices),
        recoveryFactor: Number(sim.recoveryFactor),
      });
      setSim(s => ({ ...s, result: r?.data || r, loading: false }));
    } catch (e) {
      setSim(s => ({ ...s, loading: false, result: { status: 'error', reason: e.message } }));
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Unlock calendar</h3>
        <div className="space-y-2 mb-4">
          {(project.unlocks || []).length === 0 && <div className="text-xs text-zinc-500">No unlocks recorded.</div>}
          {(project.unlocks || []).slice().sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)).map(u => (
            <div key={u.unlockId} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded p-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100">
                  <span className="font-semibold">{u.tokenSymbol}</span>
                  {' · '}{u.eventDate}{' · '}
                  {Number(u.amount).toLocaleString()} tokens
                  {u.percentOfSupply !== null && u.percentOfSupply !== undefined && <span className="text-zinc-500"> ({u.percentOfSupply}% of supply)</span>}
                </div>
                {u.notes && <div className="text-xs text-zinc-500 mt-0.5">{u.notes}</div>}
              </div>
              <button onClick={() => onRemoveUnlock(u.unlockId)} className="text-zinc-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
              <button onClick={() => setSim(s => ({ ...s, unlockId: u.unlockId, amount: String(u.amount) }))}
                className="text-emerald-400 hover:text-emerald-300 text-xs">Simulate →</button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <select value={form.tokenSymbol} onChange={e => setForm(f => ({ ...f, tokenSymbol: e.target.value }))}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100">
            <option value="">Token…</option>
            {(project.tokens || []).map(t => <option key={t.symbol} value={t.symbol}>{t.symbol}</option>)}
          </select>
          <input type="date" value={form.eventDate} onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="Amount"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" step="0.01" value={form.percentOfSupply} onChange={e => setForm(f => ({ ...f, percentOfSupply: e.target.value }))} placeholder="% of supply"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))} placeholder="Recipient (optional)"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
        </div>
        <button onClick={submitUnlock} className="mt-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded px-3 py-1.5">Add unlock</button>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" /> Unlock-impact simulator</h3>
        <p className="text-xs text-zinc-500 mb-3">
          Provide your real DEX pool reserves to model price impact under constant-product (Uniswap V2) math. We do not fabricate liquidity.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3">
          <input type="number" value={sim.amount} onChange={e => setSim(s => ({ ...s, amount: e.target.value }))} placeholder="Sell amount"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" value={sim.tokenReserve} onChange={e => setSim(s => ({ ...s, tokenReserve: e.target.value }))} placeholder="Pool token reserve"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" value={sim.quoteReserve} onChange={e => setSim(s => ({ ...s, quoteReserve: e.target.value }))} placeholder="Pool quote reserve"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input value={sim.quoteSymbol} onChange={e => setSim(s => ({ ...s, quoteSymbol: e.target.value }))} placeholder="Quote (USDC, WETH…)"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" min="2" max="48" value={sim.slices} onChange={e => setSim(s => ({ ...s, slices: e.target.value }))} placeholder="Slices"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <input type="number" step="0.05" min="0" max="1" value={sim.recoveryFactor} onChange={e => setSim(s => ({ ...s, recoveryFactor: e.target.value }))} placeholder="Recovery (0..1)"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
        </div>
        <button onClick={runSim} disabled={sim.loading || !(Number(sim.amount) > 0)}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded px-3 py-1.5">
          {sim.loading ? 'Simulating…' : 'Run simulation'}
        </button>

        {sim.result?.status === 'pending' && <div className="mt-4"><PendingBanner reason={sim.result.reason} /></div>}
        {sim.result?.status === 'error' && <div className="mt-4 text-rose-300 text-sm">{sim.result.reason}</div>}
        {sim.result?.status === 'ok' && (
          <div className="mt-5 space-y-4">
            {/* Plain-English summary — translate the percentages into what they mean */}
            <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/5 border border-teal-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-teal-300" />
                </div>
                <div className="space-y-2 flex-1 text-xs text-zinc-300 leading-relaxed">
                  <h4 className="text-sm font-bold text-zinc-100">What this unlock simulation means</h4>
                  <p>
                    If <span className="text-zinc-100 font-mono font-semibold">{Number(sim.amount).toLocaleString()}</span> tokens hit the pool in a single market sell, the price drops <span className={`font-mono font-semibold ${sim.result.scenarios.singleShotWorstCase.priceDropPct >= 50 ? 'text-rose-300' : sim.result.scenarios.singleShotWorstCase.priceDropPct >= 20 ? 'text-amber-300' : 'text-emerald-300'}`}>{sim.result.scenarios.singleShotWorstCase.priceDropPct}%</span> instantly, and the average sell slips <span className="font-mono font-semibold text-zinc-100">{sim.result.scenarios.singleShotWorstCase.priceImpactPct}%</span> below spot.
                  </p>
                  <p>
                    Staggering the sell into <span className="text-zinc-100 font-semibold">{sim.result.scenarios.staggered.slices}</span> slices with <span className="text-zinc-100 font-semibold">{Math.round((sim.result.scenarios.staggered.recoveryFactor || 0) * 100)}%</span> recovery between each cuts the net price drop to <span className="font-mono font-semibold text-emerald-300">{sim.result.scenarios.staggered.priceDropPct}%</span>. That's the difference between a controlled rebalance and a chart-breaking dump.
                  </p>
                  {sim.result.liquidityDepth?.sellSizeFor50pctDrop != null && (
                    <p className="text-zinc-400">
                      For reference: it takes <span className="text-zinc-100 font-mono">{sim.result.liquidityDepth.sellSizeFor50pctDrop.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> tokens of sell pressure to drive this pool down 50% — the deeper that number, the more pain your pool can absorb before a chart break.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatPill label="Single-shot impact" value={`${sim.result.scenarios.singleShotWorstCase.priceImpactPct}%`} hint="vs spot, pessimistic" />
              <StatPill label="Single-shot price drop" value={`${sim.result.scenarios.singleShotWorstCase.priceDropPct}%`} hint="new spot vs old" />
              <StatPill label={`Staggered (${sim.result.scenarios.staggered.slices} slices)`} value={`${sim.result.scenarios.staggered.priceDropPct}%`} hint={`recovery ${Math.round((sim.result.scenarios.staggered.recoveryFactor || 0) * 100)}%`} />
              <StatPill label="50% drop sell-size" value={sim.result.liquidityDepth?.sellSizeFor50pctDrop?.toLocaleString(undefined, { maximumFractionDigits: 0 })} hint="liquidity depth" />
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-400 space-y-1">
              <div><span className="text-zinc-500">Single-shot:</span> {sim.result.methodology.singleShotAssumption}</div>
              <div><span className="text-zinc-500">Staggered:</span> {sim.result.methodology.staggeredAssumption}</div>
              <div><span className="text-zinc-500">Depth:</span> {sim.result.methodology.depthMetric}</div>
              <div className="pt-2 border-t border-zinc-800 text-zinc-500">
                {sim.result.disclosures.notFinancialAdvice}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: API Keys ─────────────────────────────────────────────────────────

function ApiKeysTab({ project, onIssue, onRevoke }) {
  const [label, setLabel] = useState('default');
  const [issued, setIssued] = useState(null);

  async function issue() {
    const r = await onIssue(label);
    setIssued(r?.data || r);
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-200">API keys</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-3">
          Use API keys to embed your project's data on your own dashboard / status page / DD portal.
          Each key is hashed at rest — the plaintext is shown <em>once</em> on creation.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (e.g. 'production', 'investor-portal')"
            className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100" />
          <button onClick={issue} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded px-3 py-1.5">Issue key</button>
        </div>

        {issued && issued.plaintext && (
          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded p-3 mb-4">
            <div className="text-xs text-emerald-300 font-semibold mb-1">New API key — save now, it will not be shown again</div>
            <div className="font-mono text-sm text-emerald-200 break-all bg-zinc-950 border border-emerald-500/20 rounded p-2">{issued.plaintext}</div>
            <div className="text-xs text-emerald-300/70 mt-2">{issued.warning}</div>
          </div>
        )}

        <div className="space-y-2">
          {(project.apiKeys || []).length === 0 && <div className="text-xs text-zinc-500">No keys issued yet.</div>}
          {(project.apiKeys || []).map(k => (
            <div key={k.keyHash} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded p-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100">{k.label} <span className="text-zinc-500 font-mono text-xs">{k.prefix}…</span></div>
                <div className="text-xs text-zinc-500">created {new Date(k.createdAt).toLocaleDateString()} · {k.callsThisMonth || 0} calls this month</div>
              </div>
              <button onClick={() => onRevoke(k.keyHash)} className="text-zinc-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-2">Embedding endpoints</h3>
        <p className="text-xs text-zinc-500 mb-3">Send your key as <code className="bg-zinc-800 px-1 rounded">X-API-Key</code> header or <code className="bg-zinc-800 px-1 rounded">Authorization: Bearer ...</code>.</p>
        <div className="font-mono text-xs text-zinc-300 space-y-1 bg-zinc-950 border border-zinc-800 rounded p-3">
          <div><span className="text-emerald-400">GET</span>  /api/v1/tokenomics/projects/{project.projectId}</div>
          <div><span className="text-emerald-400">GET</span>  /api/v1/tokenomics/projects/{project.projectId}/holders?network=&contractAddress=</div>
          <div><span className="text-amber-400">POST</span> /api/v1/tokenomics/projects/{project.projectId}/simulate</div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Explorer Keys (BYO per chain) ────────────────────────────────────

function ExplorerKeysTab({ project, onSet, onRemove }) {
  const [editing, setEditing] = useState(null); // chain id being edited
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const stored = project.explorerApiKeys || {};

  async function save(chain) {
    if (draft.trim().length < 12) return;
    setBusy(true);
    try {
      await onSet(chain, draft.trim());
      setEditing(null);
      setDraft('');
    } finally { setBusy(false); }
  }

  async function clear(chain) {
    if (!window.confirm(`Remove the stored explorer key for ${chain}?`)) return;
    setBusy(true);
    try { await onRemove(chain); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Key className="w-4 h-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Bring-your-own explorer API keys</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-2 leading-relaxed">
          Live holder distribution data (top-10 concentration, Gini, etc.) requires a paid explorer plan
          from each chain's block explorer. Paste your own key per chain — it's encrypted at rest with
          AES-256-GCM and only used server-side when the holder analysis runs for this project.
        </p>
        <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
          <strong className="text-zinc-300">Where to get keys:</strong> sign up for a Pro tier on the
          relevant block explorer (typically $200/mo per chain, supports the
          <code className="bg-zinc-800 mx-1 px-1 rounded">tokenholderlist</code> endpoint). One key per chain
          you want to enable. Without a key, the row will say "Live holder data requires a paid explorer
          API key" on both this dashboard and the public report.
        </p>

        <div className="space-y-2">
          {EXPLORER_KEY_CHAINS.map(c => {
            const entry = stored[c.id];
            const isEditing = editing === c.id;
            return (
              <div key={c.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-zinc-100 font-medium">{c.label}</span>
                    <span className="text-[10px] text-zinc-500">via {c.provider}</span>
                    {entry?.preview ? (
                      <span className="px-2 py-0.5 text-[10px] rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono">
                        configured · {entry.preview}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] rounded bg-zinc-700/40 text-zinc-400 border border-zinc-600">not configured</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button onClick={() => { setEditing(null); setDraft(''); }} disabled={busy}
                          className="text-xs text-zinc-400 hover:text-white">Cancel</button>
                        <button onClick={() => save(c.id)} disabled={busy || draft.trim().length < 12}
                          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs rounded px-3 py-1">
                          {busy ? 'Saving…' : 'Save'}
                        </button>
                      </>
                    ) : (
                      <>
                        {entry?.preview && (
                          <button onClick={() => clear(c.id)} disabled={busy}
                            className="text-xs text-rose-400 hover:text-rose-300">Remove</button>
                        )}
                        <button onClick={() => { setEditing(c.id); setDraft(''); }}
                          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded px-3 py-1">
                          {entry?.preview ? 'Replace' : 'Add key'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing && (
                  <div className="mt-2">
                    <input
                      type="password"
                      autoFocus
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      placeholder={`Paste your ${c.provider} API key`}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100 font-mono"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">Minimum 12 characters. Stored encrypted, never displayed back in plaintext.</p>
                  </div>
                )}
                {entry?.addedAt && !isEditing && (
                  <p className="text-[10px] text-zinc-500 mt-1">Added {new Date(entry.addedAt).toLocaleDateString()}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2">
        <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          <span className="font-semibold">Security:</span> keys are encrypted at rest with AES-256-GCM
          using a per-app master key. The plaintext is never written to logs, never returned in API responses,
          and never displayed back after the first save. If you lose access to the key, just paste it again
          to replace.
        </p>
      </div>
    </div>
  );
}

// ─── Page shell ────────────────────────────────────────────────────────────

export default function TokenomicsStudioPage() {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function refreshList() {
    try {
      const r = await listTokenomicsProjects();
      const list = r?.data?.projects || [];
      setProjects(list);
      if (!selectedId && list.length > 0) setSelectedId(list[0].projectId);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function refreshProject(id = selectedId) {
    if (!id) { setProject(null); return; }
    try {
      const r = await getTokenomicsProject(id);
      setProject(r?.data || r);
    } catch (e) { setError(e.message); }
  }

  useEffect(() => { refreshList(); }, []); // eslint-disable-line
  useEffect(() => { refreshProject(selectedId); }, [selectedId]); // eslint-disable-line

  async function handleCreate(spec) {
    const r = await createTokenomicsProject(spec);
    const created = r?.data;
    await refreshList();
    if (created?.projectId) setSelectedId(created.projectId);
  }

  if (loading) return <div className="p-8 text-zinc-400">Loading…</div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1500px] mx-auto space-y-6">
      {/* Premium hero — consistent with other Elite pages */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-500/10 via-zinc-900/80 to-zinc-950 rounded-2xl border border-teal-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Tokenomics Studio</h1>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">B2B workspace</span>
              </div>
              <p className="text-xs text-teal-200/70 mt-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Elite tier · Multi-token issuer workspace — real holder analytics, unlock-impact simulator, public investor reports, API access
                <InfoTip side="bottom">Simulator REQUIRES real pool reserves (no fabricated liquidity). API key plaintext shown ONCE — store in real secret manager. "Data unavailable" is honest behavior, not a bug.</InfoTip>
              </p>
            </div>
          </div>
        </div>
      </div>

      <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />

      {error && <div className="mb-4 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-rose-200 text-sm">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <ProjectList
          projects={projects}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCreate={handleCreate}
        />

        {project ? (
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-semibold text-zinc-100">{project.name}</h2>
                  <PlanBadge plan={project.plan} />
                </div>
                <div className="text-xs text-zinc-500 font-mono">{project.projectId}</div>
              </div>
              {project.publicReportEnabled && (
                <Link to={`/tokenomics/r/${project.publicSlug}`} target="_blank" rel="noopener"
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                  Public report <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>

            <div className="flex gap-1 mb-5 border-b border-zinc-800">
              {[
                { id: 'overview',     label: 'Overview' },
                { id: 'holders',      label: 'Holders' },
                { id: 'unlocks',      label: 'Unlocks + Simulate' },
                { id: 'explorerKeys', label: 'Explorer Keys' },
                { id: 'apikeys',      label: 'API Keys' },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? 'border-emerald-500 text-emerald-300' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <OverviewTab
                project={project}
                onUpdate={async (patch) => { await updateTokenomicsProject(project.projectId, patch); await refreshProject(); }}
                onAddToken={async (spec) => { await addTokenomicsToken(project.projectId, spec); await refreshProject(); }}
                onRemoveToken={async (rm) => { await removeTokenomicsToken(project.projectId, rm); await refreshProject(); }}
                onTogglePublic={async (b) => { await updateTokenomicsProject(project.projectId, { publicReportEnabled: b }); await refreshProject(); }}
                onDelete={async () => { await deleteTokenomicsProject(project.projectId); setSelectedId(null); await refreshList(); }}
              />
            )}
            {tab === 'holders' && <HoldersTab project={project} />}
            {tab === 'explorerKeys' && (
              <ExplorerKeysTab
                project={project}
                onSet={async (chain, apiKey) => { await setTokenomicsExplorerKey(project.projectId, chain, apiKey); await refreshProject(); }}
                onRemove={async (chain) => { await removeTokenomicsExplorerKey(project.projectId, chain); await refreshProject(); }}
              />
            )}
            {tab === 'unlocks' && (
              <UnlocksTab
                project={project}
                onAddUnlock={async (u) => { await addTokenomicsUnlock(project.projectId, u); await refreshProject(); }}
                onRemoveUnlock={async (id) => { await removeTokenomicsUnlock(project.projectId, id); await refreshProject(); }}
              />
            )}
            {tab === 'apikeys' && (
              <ApiKeysTab
                project={project}
                onIssue={async (label) => { const r = await issueTokenomicsApiKey(project.projectId, { label }); await refreshProject(); return r; }}
                onRevoke={async (h) => { await revokeTokenomicsApiKey(project.projectId, h); await refreshProject(); }}
              />
            )}
          </div>
        ) : (
          <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-12 text-center text-zinc-500">
            {projects.length === 0 ? 'Create your first project on the left.' : 'Select a project to begin.'}
          </div>
        )}
      </div>
    </div>
  );
}
